import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Alert, Link, Typography } from "@mui/joy";
import { supabase } from "../lib/supabase";

export const AuthConfirmPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const tokenHash = searchParams.get("token_hash");
  const type = searchParams.get("type");
  const isValidParams =
    !!tokenHash && (type === "recovery" || type === "invite");

  useEffect(() => {
    if (!isValidParams) return;

    supabase.auth
      .verifyOtp({
        token_hash: tokenHash!,
        type: type as "recovery" | "invite",
      })
      .then(({ error }) => {
        if (error) {
          if (type === "invite") {
            setVerifyError("This invite link has expired or is invalid.");
          } else {
            setVerifyError("This reset link has expired or is invalid.");
          }
        } else {
          if (type === "invite") {
            navigate("/auth/reset-password?flow=invite", { replace: true });
          } else {
            navigate("/auth/reset-password", { replace: true });
          }
        }
      });
  }, [isValidParams, tokenHash, type, navigate]);

  const genericError = !isValidParams
    ? "Invalid or missing confirmation token."
    : null;
  const errorMessage = genericError || verifyError;

  if (errorMessage) {
    const showRecoveryLink =
      !genericError && !!verifyError && type === "recovery";
    const showInviteContact =
      !genericError && !!verifyError && type === "invite";

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#FDF8F5",
          padding: 3,
        }}
      >
        <Box sx={{ maxWidth: 400, width: "100%", textAlign: "center" }}>
          <Alert color="danger" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
          {showRecoveryLink && (
            <Typography level="body-sm">
              <Link
                component="button"
                onClick={() => navigate("/auth/forgot-password")}
                sx={{ cursor: "pointer" }}
              >
                Request a new reset link
              </Link>
            </Typography>
          )}
          {showInviteContact && (
            <Typography level="body-sm">
              Please contact the person who invited you for a new invite link.
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#FDF8F5",
      }}
    >
      <CircularProgress />
    </Box>
  );
};
